import { useState, useRef, useEffect, useCallback } from 'react';
import { isAbortError } from '@medprompt/shared';

const useDocumentDuplicates = ({ doc, documentService, getDocScopeId, docScopeOptions, loadKey }) => {
  const [duplicateDocumentIds, setDuplicateDocumentIds] = useState([]);
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  // Show duplicates modal only once per document per session
  const duplicatesShownRef = useRef(new Set());

  const docId = doc?.documentId;
  const docPatientId = doc?.patientId;

  useEffect(() => {
    setDuplicateDocumentIds([]);
    setShowDuplicatesModal(false);
  }, [loadKey]);

  // Fetch duplicate documents when document is loaded or when patientId arrives via SSE.
  // Duplicate detection short-circuits without a matched patient, so this mirrors the
  // pending referrals pattern: re-run whenever patientId transitions null → value.
  useEffect(() => {
    if (docId && docPatientId) {
      const requestId = getDocScopeId();
      const targetDocumentId = docId;
      const fetchDuplicates = async () => {
        try {
          const duplicateIds = await documentService.fetchDocumentDuplicates(
            targetDocumentId, docScopeOptions());
          if (requestId !== getDocScopeId()) {
            return;
          }

          const otherDuplicates = duplicateIds.filter(id => id !== targetDocumentId);

          if (otherDuplicates.length > 0) {
            setDuplicateDocumentIds(otherDuplicates);
            if (!duplicatesShownRef.current.has(targetDocumentId)) {
              duplicatesShownRef.current.add(targetDocumentId);
              setShowDuplicatesModal(true);
            }
          } else {
            setDuplicateDocumentIds([]);
            setShowDuplicatesModal(false);
          }
        } catch (error) {
          if (isAbortError(error) || requestId !== getDocScopeId()) return;
          console.error('Error fetching duplicate documents:', error);
          setDuplicateDocumentIds([]);
          setShowDuplicatesModal(false);
        }
      };

      fetchDuplicates();
    } else if (!docId) {
      setDuplicateDocumentIds([]);
      setShowDuplicatesModal(false);
    }
    // If docId is present but patientId is not yet available, duplicate state was already
    // cleared when the load started. Stay empty until patientId arrives and this effect re-runs.
  }, [docId, docPatientId, documentService, getDocScopeId, docScopeOptions]);

  const openDuplicatesModal = useCallback(() => setShowDuplicatesModal(true), []);
  const closeDuplicatesModal = useCallback(() => setShowDuplicatesModal(false), []);
  const removeDuplicate = useCallback((deletedId) => {
    setDuplicateDocumentIds(prev => prev.filter(id => id !== deletedId));
  }, []);

  return {
    duplicateDocumentIds,
    showDuplicatesModal,
    openDuplicatesModal,
    closeDuplicatesModal,
    removeDuplicate,
  };
};

export default useDocumentDuplicates;
